/**
 * Render the hybrid engine's `Op[]` model to track-changes HTML for the preview card.
 *
 * This is half of the "one diff, two renderers" design: the SAME `Op[]` that the apply executes is
 * rendered here as HTML, so the preview shows exactly what will be applied (WYSIWYG). Uses the same
 * `ins.track-change-insertion` / `del.track-change-deletion` classes the existing preview styles.
 *
 * **Coloring (matches the legacy author-aware preview):**
 *  - A NEW `insert` op is text we wrote → `track-change-new` → **blue**.
 *  - A NEW `delete` op is our act on someone else's words: the removed text keeps the colour of
 *    whoever authored it, so deleting a counterparty revision still reads as theirs.
 *  - EXISTING revisions live inside `keep` runs (the engine preserves them, see `extractRevisions`).
 *    When a `revisionContext` is supplied, keep text that falls inside an existing revision is
 *    rendered as `ins`/`del` too: **red** for the counterparty (`side: 'theirs'`), **blue** for our
 *    own prior edits (`side: 'ours'`). Plain context is escaped text.
 *
 * Without a `revisionContext` the renderer is unchanged: keeps are plain, new ops blue.
 *
 * Pure, no Word dependency, unit-tested.
 */

import { Op, RevisionContext, RevisionSpan } from './types';

/** Marks an edit as our-side/new → the preview CSS renders it blue (vs red for counterparty). */
const NEW = 'track-change-new';

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Render one existing-revision segment: its kind picks ins/del, its side picks red (theirs) vs blue. */
function renderRevisionSegment(text: string, revision: RevisionSpan): string {
  const newClass = revision.side === 'ours' ? ` ${NEW}` : '';
  const cls = revision.kind === 'insertion' ? 'track-change-insertion' : 'track-change-deletion';
  const tag = revision.kind === 'insertion' ? 'ins' : 'del';
  return `<${tag} class="${cls}${newClass}">${escapeHtml(text)}</${tag}>`;
}

/**
 * Split a baseline run at every revision boundary inside it and hand each piece
 * to `render`, together with the revision that owns it (if any).
 */
function renderOverBaseline(
  text: string,
  runStart: number,
  revisions: RevisionSpan[],
  render: (segment: string, revision: RevisionSpan | undefined) => string,
): string {
  const runEnd = runStart + text.length;
  const overlapping = revisions.filter((r) => r.end > runStart && r.start < runEnd);
  if (overlapping.length === 0) return render(text, undefined);

  const points = new Set<number>([runStart, runEnd]);
  for (const r of overlapping) {
    points.add(Math.max(runStart, r.start));
    points.add(Math.min(runEnd, r.end));
  }
  const sorted = [...points].sort((a, b) => a - b);

  let html = '';
  for (let i = 0; i < sorted.length - 1; i++) {
    const from = sorted[i];
    const to = sorted[i + 1];
    const seg = text.slice(from - runStart, to - runStart);
    if (seg === '') continue;
    html += render(seg, overlapping.find((r) => r.start <= from && to <= r.end));
  }
  return html;
}

/**
 * Render a `delete` op — text we are removing.
 *
 * The deletion is our act, but the words are not necessarily ours. Where the
 * removed text sits inside a revision the counterparty authored, it stays in
 * their colour: a clause the other side wrote must not read as ours merely
 * because our edit removes it. Text with no revision behind it is original
 * document content, and removing that is our edit alone.
 */
function renderDelete(text: string, deleteStart: number, revisions: RevisionSpan[]): string {
  if (revisions.length === 0) {
    return `<del class="track-change-deletion ${NEW}">${escapeHtml(text)}</del>`;
  }
  return renderOverBaseline(text, deleteStart, revisions, (seg, rev) => {
    const theirs = rev?.side === 'theirs';
    const cls = theirs ? 'track-change-deletion' : `track-change-deletion ${NEW}`;
    return `<del class="${cls}">${escapeHtml(seg)}</del>`;
  });
}

/** Render a `keep` run, overlaying any existing revisions that fall within it. */
function renderKeep(text: string, keepStart: number, revisions: RevisionSpan[]): string {
  const keepEnd = keepStart + text.length;
  const overlapping = revisions.filter((r) => r.end > keepStart && r.start < keepEnd);
  if (overlapping.length === 0) return escapeHtml(text);

  // Split the keep at every revision boundary that lands inside it, then render each piece.
  const points = new Set<number>([keepStart, keepEnd]);
  for (const r of overlapping) {
    points.add(Math.max(keepStart, r.start));
    points.add(Math.min(keepEnd, r.end));
  }
  const sorted = [...points].sort((a, b) => a - b);

  let html = '';
  for (let i = 0; i < sorted.length - 1; i++) {
    const from = sorted[i];
    const to = sorted[i + 1];
    const seg = text.slice(from - keepStart, to - keepStart);
    if (seg === '') continue;
    const rev = overlapping.find((r) => r.start <= from && to <= r.end);
    html += rev ? renderRevisionSegment(seg, rev) : escapeHtml(seg);
  }
  return html;
}

/**
 * Render ops (baseline order) to HTML. New `delete`/`insert` ops are blue (our-side). `keep` text is
 * plain, except where an existing revision (from `revisionContext`) overlays it — then rendered as a
 * red (theirs) or blue (ours) ins/del.
 */
export function opsToTrackChangesHtml(ops: Op[], revisionContext?: RevisionContext): string {
  const revisions = revisionContext?.revisions ?? [];
  let html = '';
  let cursor = 0; // offset into the baseline (keep/delete advance it, insert does not)
  for (const op of ops) {
    const text = escapeHtml(op.value);
    if (op.kind === 'insert') {
      html += `<ins class="track-change-insertion ${NEW}">${text}</ins>`;
    } else if (op.kind === 'delete') {
      html += renderDelete(op.value, cursor, revisions);
      cursor += op.value.length;
    } else {
      html += revisions.length > 0 ? renderKeep(op.value, cursor, revisions) : text;
      cursor += op.value.length;
    }
  }
  return html;
}
